import { useCallback, useEffect, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Modal, Nav, Row, Table } from 'react-bootstrap';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../components/common/PageHeader';
import Loader from '../components/common/Loader';
import EmptyState from '../components/common/EmptyState';
import PatientForm from '../components/patient/PatientForm';
import StartVisitModal from '../components/patient/StartVisitModal';
import AttachmentField from '../components/patient/AttachmentField';
import PaymentCard from '../components/patient/PaymentCard';
import ConfirmModal from '../components/common/ConfirmModal';
import { patientApi, uploadApi } from '../api';
import { useAuth } from '../context/AuthContext';
import { useClinicEvent } from '../context/SocketContext';
import { useToast } from '../context/ToastContext';
import { fileUrl, formatAge, formatDate, formatMoney, formatTime, timeAgo, titleCase } from '../utils/format';

function VitalsLine({ vitals }) {
  if (!vitals) return null;
  const parts = [
    vitals.weight && `Weight ${vitals.weight} kg`,
    vitals.bpSystolic && `BP ${vitals.bpSystolic}/${vitals.bpDiastolic}`,
    vitals.pulse && `Pulse ${vitals.pulse}`,
    vitals.temperature && `Temp ${vitals.temperature} F`,
    vitals.spo2 && `SpO2 ${vitals.spo2}%`,
    vitals.sugar && `Sugar ${vitals.sugar}`,
  ].filter(Boolean);

  if (!parts.length) return null;
  return <div className="text-muted small">{parts.join('  |  ')}</div>;
}

export default function PatientProfile() {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { can, isDoctor } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('timeline');
  const [editing, setEditing] = useState(false);
  const [startVisit, setStartVisit] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [addingReports, setAddingReports] = useState(false);
  const [pendingReports, setPendingReports] = useState([]);
  const [deletingReport, setDeletingReport] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await patientApi.history(id);
      setData(res.data);
      // Newest visit opens by default - that is what staff look at first.
      setExpanded((cur) => cur ?? res.data.timeline[0]?._id ?? null);
    } catch (err) {
      toast.error(err);
    } finally {
      setLoading(false);
    }
  }, [id, toast]);

  useEffect(() => {
    load();
  }, [load]);

  useClinicEvent(['queue:changed', 'consultation:saved', 'patient:updated'], load);

  if (loading) return <Loader label="Loading patient record..." className="py-5" />;
  if (!data) return <Alert variant="danger">Patient not found.</Alert>;

  const { patient, timeline } = data;
  const openVisit = timeline.find((v) => v.status === 'waiting' || v.status === 'in-consultation');

  async function handleUpdate(values) {
    try {
      await patientApi.update(patient._id, values);
      toast.success('Patient details updated');
      setEditing(false);
      load();
    } catch (err) {
      toast.error(err);
      throw err;
    }
  }

  async function saveReports() {
    try {
      await patientApi.addReport(patient._id, { reports: pendingReports });
      toast.success(`${pendingReports.length} attachment${pendingReports.length === 1 ? '' : 's'} saved`);
      setPendingReports([]);
      setAddingReports(false);
      load();
    } catch (err) {
      toast.error(err);
    }
  }

  /** Discards anything uploaded but not saved, so no orphan files are left. */
  function cancelReports() {
    pendingReports.forEach((r) => uploadApi.discard(r).catch(() => {}));
    setPendingReports([]);
    setAddingReports(false);
  }

  return (
    <>
      <PageHeader
        title={patient.name}
        subtitle={`Registered ${formatDate(patient.createdAt)}`}
        icon="bi-person-vcard"
      >
        <Button variant="light" onClick={() => navigate(-1)}>
          <i className="bi bi-arrow-left me-1" /> Back
        </Button>
        {can('reception', 'doctor') && (
          <Button variant="outline-secondary" onClick={() => setEditing(true)}>
            <i className="bi bi-pencil me-1" /> Edit
          </Button>
        )}
        {openVisit ? (
          <Button
            className="btn-clinic"
            onClick={() => (isDoctor ? navigate(`/consultation/${openVisit._id}`) : navigate('/queue'))}
          >
            <i className="bi bi-arrow-right-circle me-1" />
            In queue on token {openVisit.tokenNo}
          </Button>
        ) : (
          can('reception') && (
            <Button className="btn-clinic" onClick={() => setStartVisit(true)}>
              <i className="bi bi-clipboard2-plus me-1" /> Start New Visit
            </Button>
          )
        )}
      </PageHeader>

      {patient.allergies?.length > 0 && (
        <div className="allergy-alert mb-3">
          <i className="bi bi-exclamation-triangle-fill me-2" />
          <strong>Allergic to:</strong> {patient.allergies.join(', ')}
        </div>
      )}

      <Row className="g-4">
        <Col lg={4}>
          <Card className="mb-4">
            <Card.Header>
              <i className="bi bi-person-lines-fill me-2" />
              Patient Details
            </Card.Header>
            <Card.Body className="small">
              <dl className="dl-clean">
                <div>
                  <dt>Mobile</dt>
                  <dd>{patient.mobile}</dd>
                </div>
                <div>
                  <dt>Age / Gender</dt>
                  <dd>
                    {formatAge(patient)} &middot; {titleCase(patient.gender)}
                  </dd>
                </div>
                <div>
                  <dt>Weight</dt>
                  <dd>{patient.weight ? `${patient.weight} kg` : '-'}</dd>
                </div>
                <div>
                  <dt>Blood Group</dt>
                  <dd>{patient.bloodGroup || '-'}</dd>
                </div>
                <div>
                  <dt>Address</dt>
                  <dd>{[patient.address, patient.city].filter(Boolean).join(', ') || '-'}</dd>
                </div>
                <div>
                  <dt>Total Visits</dt>
                  <dd>{patient.totalVisits}</dd>
                </div>
                <div>
                  <dt>Last Visit</dt>
                  <dd>
                    {patient.lastVisitDate
                      ? `${formatDate(patient.lastVisitDate)} (${timeAgo(patient.lastVisitDate)})`
                      : '-'}
                  </dd>
                </div>
              </dl>

              {patient.chronicConditions?.length > 0 && (
                <>
                  <hr />
                  <div className="text-muted mb-1">Long-standing conditions</div>
                  <div className="d-flex flex-wrap gap-1">
                    {patient.chronicConditions.map((c) => (
                      <Badge bg="warning" text="dark" key={c}>
                        {c}
                      </Badge>
                    ))}
                  </div>
                </>
              )}

              {patient.notes && (
                <>
                  <hr />
                  <div className="text-muted mb-1">Notes</div>
                  <div>{patient.notes}</div>
                </>
              )}
            </Card.Body>
          </Card>

          <PaymentCard payment={data.payment} timeline={timeline} onChanged={load} />

          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>
                <i className="bi bi-paperclip me-2" />
                Reports ({patient.reports?.length || 0})
              </span>
              {can('reception', 'doctor') && !addingReports && (
                <Button size="sm" variant="outline-primary" onClick={() => setAddingReports(true)}>
                  <i className="bi bi-plus-lg me-1" /> Add
                </Button>
              )}
            </Card.Header>
            <Card.Body>
              {addingReports && (
                <div className="mb-3">
                  <AttachmentField value={pendingReports} onChange={setPendingReports} compact />
                  <div className="d-flex gap-2 justify-content-end mt-2">
                    <Button size="sm" variant="light" onClick={cancelReports}>
                      Cancel
                    </Button>
                    <Button size="sm" className="btn-clinic" disabled={!pendingReports.length} onClick={saveReports}>
                      Save {pendingReports.length || ''} attachment{pendingReports.length === 1 ? '' : 's'}
                    </Button>
                  </div>
                </div>
              )}

              {patient.reports?.length ? (
                <div className="attach-list">
                  {patient.reports.map((r) => (
                    <div className="attach-item" key={r._id}>
                      <i
                        className={`bi ${r.type === 'pdf' ? 'bi-file-earmark-pdf' : 'bi-file-earmark-image'} attach-icon`}
                      />
                      <div className="flex-grow-1 min-w-0">
                        <a href={fileUrl(r.url)} target="_blank" rel="noreferrer" className="attach-name">
                          {r.name}
                        </a>
                        <div className="text-muted" style={{ fontSize: '0.74rem' }}>
                          {formatDate(r.uploadedAt)}
                          {r.sizeKb ? ` · ${(r.sizeKb / 1024).toFixed(1)} MB` : ''}
                        </div>
                        {r.note && <div className="small">{r.note}</div>}
                      </div>
                      {can('reception', 'doctor') && (
                        <Button
                          size="sm"
                          variant="outline-danger"
                          className="btn-icon flex-shrink-0"
                          onClick={() => setDeletingReport(r)}
                          title={`Remove ${r.name}`}
                          aria-label={`Remove ${r.name}`}
                        >
                          <i className="bi bi-trash" />
                        </Button>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                !addingReports && (
                  <div className="text-muted small text-center py-2">
                    No sonography or lab reports attached yet.
                  </div>
                )
              )}
            </Card.Body>
          </Card>
        </Col>

        <Col lg={8}>
          <Card>
            <Card.Header className="pb-0 pt-2">
              <Nav variant="tabs" activeKey={tab} onSelect={setTab}>
                <Nav.Item>
                  <Nav.Link eventKey="timeline">
                    <i className="bi bi-clock-history me-1" />
                    Visit History ({timeline.length})
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item>
                  <Nav.Link eventKey="medicines">
                    <i className="bi bi-capsule me-1" />
                    All Medicines Given
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Card.Header>

            <Card.Body>
              {tab === 'timeline' &&
                (timeline.length === 0 ? (
                  <EmptyState
                    icon="bi-clipboard"
                    title="No visits recorded yet"
                    hint="Start a visit to add this patient to the doctor's queue."
                  />
                ) : (
                  timeline.map((visit) => {
                    const c = visit.consultation;
                    const open = expanded === visit._id;
                    return (
                      <div className="history-entry" key={visit._id}>
                        <div
                          className="d-flex flex-wrap justify-content-between align-items-start gap-2"
                          style={{ cursor: 'pointer' }}
                          onClick={() => setExpanded(open ? null : visit._id)}
                        >
                          <div>
                            <div className="fw-bold">
                              <i className={`bi ${open ? 'bi-chevron-down' : 'bi-chevron-right'} me-1 small`} />
                              {formatDate(visit.visitDate)}
                              <span className="text-muted fw-normal small ms-2">
                                {formatTime(visit.visitDate)} &middot; Token {visit.tokenNo} &middot; {timeAgo(visit.visitDate)}
                              </span>
                            </div>
                            <div className="small">
                              {c?.diagnosis?.length ? (
                                <span className="fw-semibold" style={{ color: 'var(--clinic-primary)' }}>
                                  {c.diagnosis.join(', ')}
                                </span>
                              ) : (
                                <span className="text-muted">{visit.reasonForVisit || 'No diagnosis recorded'}</span>
                              )}
                              {c?.medicines?.length ? (
                                <span className="text-muted"> &middot; {c.medicines.length} medicines</span>
                              ) : null}
                            </div>
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <Badge
                              bg={
                                visit.status === 'completed'
                                  ? 'success'
                                  : visit.status === 'cancelled'
                                    ? 'light'
                                    : 'warning'
                              }
                              className={visit.status === 'cancelled' ? 'text-dark' : ''}
                            >
                              {titleCase(visit.status)}
                            </Badge>
                            {/* Reopens the consultation screen for this visit, so
                                medicines can be corrected or added afterwards. */}
                            {isDoctor && visit.status !== 'cancelled' && (
                              <Button
                                size="sm"
                                variant="outline-primary"
                                title={c ? 'Edit this prescription' : 'Write the prescription'}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/consultation/${visit._id}`);
                                }}
                              >
                                <i className="bi bi-pencil-square" />
                              </Button>
                            )}
                            {c && (
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                title="Print this prescription"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  navigate(`/print/${visit._id}`);
                                }}
                              >
                                <i className="bi bi-printer" />
                              </Button>
                            )}
                          </div>
                        </div>

                        {open && (
                          <div className="mt-2 ps-1">
                            <VitalsLine vitals={visit.vitals} />

                            {!c ? (
                              <div className="text-muted small fst-italic mt-2">
                                The doctor has not saved a prescription for this visit.
                              </div>
                            ) : (
                              <>
                                {c.complaints?.length > 0 && (
                                  <div className="mt-2">
                                    <span className="text-muted small">Complaints: </span>
                                    <span className="small">
                                      {c.complaints.map((x) => `${x.text}${x.duration ? ` (${x.duration})` : ''}`).join(', ')}
                                    </span>
                                  </div>
                                )}

                                {c.examination && (
                                  <div className="mt-1">
                                    <span className="text-muted small">Examination: </span>
                                    <span className="small">{c.examination}</span>
                                  </div>
                                )}

                                {c.medicines?.length > 0 && (
                                  <Table size="sm" bordered className="mt-2 mb-2" style={{ fontSize: '0.82rem' }}>
                                    <thead>
                                      <tr>
                                        <th>Medicine</th>
                                        <th>Dose</th>
                                        <th>When</th>
                                        <th>Days</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {c.medicines.map((m, i) => (
                                        <tr key={i}>
                                          <td className="fw-semibold">
                                            {m.type} {m.name}
                                          </td>
                                          <td>{m.frequency}</td>
                                          <td>{m.timing}</td>
                                          <td>{m.duration}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </Table>
                                )}

                                {c.precautions?.length > 0 && (
                                  <div className="mt-1">
                                    <span className="text-muted small">Advice: </span>
                                    <span className="small">{c.precautions.join(' · ')}</span>
                                  </div>
                                )}

                                {c.followUpDate && (
                                  <div className="mt-1 small">
                                    <i className="bi bi-calendar-check me-1" />
                                    Follow-up on <strong>{formatDate(c.followUpDate)}</strong>
                                  </div>
                                )}

                                <div className="text-muted mt-2" style={{ fontSize: '0.75rem' }}>
                                  Seen by {c.doctor?.name || 'doctor'}
                                  {visit.fees?.total
                                    ? ` · Fee ${formatMoney(visit.fees.total)} (${visit.fees.status})`
                                    : ''}
                                  {/* Where the amount came from, when medicines were given out too. */}
                                  {visit.fees?.medicines > 0
                                    ? ` — ${formatMoney(visit.fees.consultation)} consultation + ${formatMoney(
                                        visit.fees.medicines,
                                      )} medicines`
                                    : ''}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })
                ))}

              {tab === 'medicines' && <AllMedicines timeline={timeline} />}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={editing} onHide={() => setEditing(false)} size="xl" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="h5">Edit {patient.name}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <PatientForm
            initial={patient}
            submitLabel="Save Changes"
            onSubmit={handleUpdate}
            onCancel={() => setEditing(false)}
          />
        </Modal.Body>
      </Modal>

      <StartVisitModal
        show={startVisit}
        patient={patient}
        onHide={() => setStartVisit(false)}
        onDone={() => navigate('/queue')}
      />

      <ConfirmModal
        show={!!deletingReport}
        title="Remove this attachment?"
        body={deletingReport ? `"${deletingReport.name}" will be deleted permanently.` : null}
        confirmLabel="Remove"
        onHide={() => setDeletingReport(null)}
        onConfirm={async () => {
          try {
            await patientApi.removeReport(patient._id, deletingReport._id);
            toast.success('Attachment removed');
            load();
          } catch (err) {
            toast.error(err);
          }
        }}
      />
    </>
  );
}

/** Every medicine this patient has ever been given, newest first. */
function AllMedicines({ timeline }) {
  const rows = timeline
    .filter((v) => v.consultation?.medicines?.length)
    .flatMap((v) =>
      v.consultation.medicines.map((m) => ({ ...m, date: v.visitDate, diagnosis: v.consultation.diagnosis })),
    );

  if (!rows.length) {
    return <EmptyState icon="bi-capsule" title="No medicines prescribed yet" />;
  }

  return (
    <div className="table-responsive">
      <Table hover size="sm" className="align-middle" style={{ fontSize: '0.87rem' }}>
        <thead>
          <tr>
            <th>Date</th>
            <th>Medicine</th>
            <th>Dose &amp; Frequency</th>
            <th>Duration</th>
            <th>For</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((m, i) => (
            <tr key={i}>
              <td className="text-muted small">{formatDate(m.date)}</td>
              <td className="fw-semibold">
                {m.type} {m.name}
              </td>
              <td>
                {m.frequency} &middot; {m.timing}
              </td>
              <td>{m.duration}</td>
              <td className="text-muted small">{m.diagnosis?.join(', ') || '-'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </div>
  );
}
